import { useState, useEffect, useCallback } from "react";
import { Formik, Form, FieldArray, ErrorMessage } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import {
  createHousingExpense,
  updateHousingExpense,
} from "containers/housing-expense-requests/api";
import DateInput from "components/common/date-picker";
import SelectComponent from "components/common/select";
import Input from "components/common/input";
import { DownloadSimple, Plus, Trash } from "phosphor-react";
import {
  expenseRequestInitialValues,
  validationSchema,
} from "../../constants/expenseRequest";
import { formatDate, formatString } from "utils/common";
import {
  getExpenseSubCategories,
  getExpenseCategories,
  getApprovalConfigurations,
} from "containers/configuration/api";
import moment from "moment";
import { initialMetaForTable, normalizeInput } from "constants/common";
import AsyncSelect from "components/common/select/AsyncSelect";
import {
  useDebouncedCategoriesDataCallback,
  useDebouncedPaymentMethodsDataCallback,
  useDebouncedSubCategoriesCallback,
} from "utils/debouncedHelper";
import CustomModal from "components/common/modal";
// const fetchAllExpenseCategories = async () => {
//   let page = 1;
//   let all = [];
//   let hasMore = true;

//   while (hasMore) {
//     const res = await getExpenseCategories({ page });

//     if (res?.expense_categories?.length) {
//       all = all.concat(res.expense_categories);
//       page += 1;
//     } else {
//       hasMore = false;
//     }
//   }

//   return all;
// };

// const fetchAllExpenseSubCategories = async () => {
//   let page = 1;
//   let all = [];
//   let hasMore = true;

//   while (hasMore) {
//     const res = await getExpenseSubCategories({ page });

//     if (res?.expense_sub_categories?.length) {
//       all = all.concat(res.expense_sub_categories);
//       page += 1;
//     } else {
//       hasMore = false;
//     }
//   }

//   return all;
// };

const CreateHousingExpenseForm = ({
  selectedMember,
  expenseRecord,
  onClose,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [allSubcategoryOptions, setAllSubcategoryOptions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [action, setAction] = useState("");
  const [showOverLimitModal, setShowOverLimitModal] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const debounceCategoryDataCallBack = useDebouncedCategoriesDataCallback();
  const debounceSubCategoryDataCallBack = useDebouncedSubCategoriesCallback();
  const debouncePaymentMethod = useDebouncedPaymentMethodsDataCallback();
  const normalizeKey = (name) =>
    name
      ?.toString()
      ?.toLowerCase()
      ?.replace(/\s+/g, "_")
      ?.replace(/[^a-z0-9_]/g, "");

  const getDepartmentCode = (state, county) => {
    if (!state || state.toUpperCase() !== "CA") return "";
    if (!county) return "";

    const countyClean = county.toLowerCase().trim();

    if (countyClean.includes("los angeles")) return "7101";
    if (countyClean.includes("riverside")) return "7301";
    if (countyClean.includes("orange")) return "7201";
    if (countyClean.includes("san bernardino")) return "7301";

    return "";
  };

  const convertFileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const normalizeOption = useCallback((name) => {
    if (!name) return null;
    const clean = name.toString().trim();
    return {
      value: clean
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, ""),
      label: clean,
    };
  }, []);

  const fetchExpenseSubCategories = async (categoryId) => {
    if (!categoryId || subcategories[categoryId]) return;
    setLoadingCategories(true);
    try {
      const result = await getExpenseSubCategories({
        ...initialMetaForTable,
        expense_request_id: categoryId,
        status: "active",
      });
      if (result?.expense_sub_categories) {
        const options = result.expense_sub_categories
          .map((item) => ({
            label: item.name,
            value: item.id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setSubcategories((prev) => ({
          ...prev,
          [categoryId]: options,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch subcategories:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchExpenseCategories = async () => {
    setLoadingCategories(true);
    try {
      const result = await getExpenseCategories({
        ...initialMetaForTable,
        status: "active",
      });
      if (result["expense_categories"]) {
        const activeCategories = result.expense_categories
          .filter((category) => category.status === "active")
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((category) => ({
            label: category.name,
            value: category.id,
          }));
        setCategories(activeCategories);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchExpenseCategories();
  }, []);
  // useEffect(() => {
  //   const loadSubcategoriesForExistingExpenses = async () => {
  //     if (!expenseRecord?.expense_informations || categories.length === 0)
  //       return;
  //     const uniqueCategories = new Set();
  //     expenseRecord.expense_informations.forEach((e) => {
  //       if (e.category) {
  //         uniqueCategories.add(e.category);
  //       }
  //     });

  //     for (const categoryName of uniqueCategories) {
  //       const categoryObj = categories.find(
  //         (cat) => cat.label === categoryName,
  //       );
  //       if (categoryObj?.value) {
  //         await fetchExpenseSubCategories(categoryObj.value);
  //       }
  //     }
  //   };

  //   if (expenseRecord && categories.length > 0) {
  //     loadSubcategoriesForExistingExpenses();
  //   }
  // }, [expenseRecord, categories]);

  useEffect(() => {
    if (!expenseRecord || categories.length === 0) return;

    expenseRecord.expense_informations.forEach((item) => {
      const categoryOption =
        categories.find((c) => c.label === item.category) || null;

      if (categoryOption?.value) {
        fetchExpenseSubCategories(categoryOption.value);
      }
    });
  }, [expenseRecord, categories]);

  useEffect(() => {
    const loadPaymentMethods = async () => {
      const result = await getApprovalConfigurations({
        ...initialMetaForTable,
        status: "active",
      });
      if (result?.approval_configurations) {
        const activeMethods = result.approval_configurations
          .filter((item) => item.status === "active")
          .map((item) => ({
            label: item?.approval_method,
            value: item?.id,
          }));

        activeMethods.sort((a, b) => a.label.localeCompare(b.label));
        setPaymentMethods(activeMethods);
      }
    };
    loadPaymentMethods();
  }, []);
  const healthPlanOptions = [
    { value: "plan1", label: "Health Plan A" },
    { value: "plan2", label: "Health Plan B" },
  ];

  const caseManagerOptions = [
    { value: "manager1", label: "Jane Smith" },
    { value: "manager2", label: "John Doe" },
  ];

  const [authorizations, setAuthorizations] = useState([]);

  useEffect(() => {
    const loadAuthorizations = async () => {
      if (selectedMember?.expense_authorizations) {
        return selectedMember.expense_authorizations.map((auth) => ({
          type: auth.auth_type,
          authNo: auth.auth_no,
          startDate: auth.start_date,
          endDate: auth.end_date,
          approvedDays: auth.approved_days,
          authorizedAmount: parseFloat(auth.authorized_amount) || 0,
        }));
      }
      return [];
    };

    if (selectedMember) {
      loadAuthorizations().then((auths) => setAuthorizations(auths));
    }
  }, [selectedMember]);

  const findMatchingOption = useCallback(
    (searchValue, optionsList) => {
      if (!searchValue) return null;
      const searchClean = searchValue
        .toString()
        .toLowerCase()
        .trim();
      const exactMatch = optionsList.find(
        (option) =>
          option.value ===
          searchClean.replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, ""),
      );
      return exactMatch || normalizeOption(searchValue);
    },
    [normalizeOption],
  );
  const formatDate = (dateInput) => {
    if (!dateInput) return "";

    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return "";

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  };
  const Dateformat = (data) => {
    const formated = moment(data, "YYYY/MM/DD").format("DD/MM/YYYY");
    return formated;
  };

  const renderUploadBox = (
    name,
    label,
    setFieldValue,
    isRequired,
    values,
    disabled = false,
  ) => {
    const fileValue = values?.[name];
    return (
      <div className="mb-4">
        <label className="fw-semibold mb-2 d-block">
          {label} {isRequired && <span style={{ color: "red" }}>*</span>}
        </label>
        <div className="border border-warning rounded p-4 text-center bg-light h-100">
          {!fileValue ? (
            <>
              <div className="mb-2">
                <i className="bi bi-upload" style={{ fontSize: "24px" }}></i>
              </div>

              <span>Drag & drop or </span>
              <label
                className="text-warning fw-bold"
                style={{ cursor: "pointer" }}
              >
                choose file
                <input
                  type="file"
                  hidden
                  onChange={(e) =>
                    setFieldValue(name, {
                      file: e.target.files[0],
                      url: null,
                      name: e.target.files[0]?.name,
                    })
                  }
                />
              </label>
              <div className="text-muted small mt-1">(Max. 2 MB)</div>
            </>
          ) : (
            <>
              <span>Drag & drop or </span>
              <label
                className="text-warning fw-bold"
                style={{ cursor: "pointer" }}
              >
                choose file
                <input
                  type="file"
                  hidden
                  disabled={disabled}
                  onChange={(e) =>
                    setFieldValue(name, {
                      file: e.target.files[0],
                      url: null,
                      name: e.target.files[0]?.name,
                    })
                  }
                />
              </label>
              <div className="text-muted small mt-1">(Max. 2 MB)</div>

              <Trash
                size={20}
                className={`cursor-pointer ${
                  disabled ? "opacity-25" : "opacity-50"
                }`}
                onClick={() => {
                  if (!disabled) setFieldValue(name, null);
                }}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <Formik
      initialValues={{
        ...expenseRequestInitialValues,
        additional_comments:
          expenseRecord?.comment || expenseRecord?.additional_comments || "",
        status: expenseRecord?.status || "",
        upload_w9: expenseRecord?.upload_w9
          ? {
              file: null,
              url: expenseRecord.upload_w9,
              name: expenseRecord.upload_w9_file_name,
            }
          : null,
        upload_lease_agreement: expenseRecord?.upload_lease_agreement
          ? {
              file: null,
              url: expenseRecord.upload_lease_agreement,
              name: expenseRecord.upload_lease_agreement_file_name,
            }
          : null,
        upload_authorization: expenseRecord?.upload_authorization
          ? {
              file: null,
              url: expenseRecord.upload_authorization,
              name: expenseRecord.upload_authorization_file_name,
            }
          : null,
        check_request_document: expenseRecord?.check_request_document
          ? {
              file: null,
              url: expenseRecord.check_request_document,
              name: expenseRecord.check_request_file_name,
            }
          : null,
        other_document: expenseRecord?.other_document
          ? {
              file: null,
              url: expenseRecord.other_document,
              name: expenseRecord.other_document_file_name,
            }
          : null,
        upload_receipt: expenseRecord?.upload_receipt
          ? {
              file: null,
              url: expenseRecord.upload_receipt,
              name: expenseRecord.upload_receipt_file_name,
            }
          : null,

        ...(() => {
          const homeAddress = selectedMember?.addresses?.find(
            (addr) => addr.address_type === "home",
          );
          const stateAbbr = homeAddress?.state?.name?.toUpperCase();
          const countyName = homeAddress?.county?.name || "";

          return {
            county:
              expenseRecord?.addresses?.find(
                (addr) => addr.address_type === "home",
              )?.county?.name ||
              formatString(homeAddress?.county?.name) ||
              "N/A",
            department_code:
              expenseRecord?.dept_code ||
              expenseRecord?.department_code ||
              getDepartmentCode(stateAbbr, countyName) ||
              "",
          };
        })(),
        member_name: expenseRecord?.member
          ? `${expenseRecord.member.first_name || ""} ${expenseRecord.member
              .last_name || ""}`.trim()
          : `${selectedMember.first_name || ""} ${selectedMember.last_name ||
              ""}`.trim(),
        date_of_birth: expenseRecord?.member?.date_of_birth
          ? Dateformat(expenseRecord.member.date_of_birth)
          : selectedMember?.date_of_birth
          ? formatDate(selectedMember.date_of_birth)
          : "",
        cin: expenseRecord?.member?.cin || selectedMember.cin || "",
        health_plan: expenseRecord?.member?.health_plan?.name
          ? {
              value: expenseRecord.member.health_plan.name,
              label: expenseRecord.member.health_plan.name,
            }
          : selectedMember?.health_plan?.name
          ? {
              value: selectedMember.health_plan.name,
              label: selectedMember.health_plan.name,
            }
          : null,

        case_manager: selectedMember?.cs_manager
          ? {
              value: selectedMember.cs_manager.id,
              label: `${selectedMember.cs_manager.first_name ||
                ""} ${selectedMember.cs_manager.last_name || ""}`.trim(),
            }
          : null,
        landlord_name: selectedMember.landlord_name || "",
        additional_comments: expenseRecord?.comment || "",
        authorizations: expenseRecord?.authorizations || [],
        expenses:
          expenseRecord?.expense_informations?.map((e) => {
            // const categoryOption = normalizeOption(e.category);
            const categoryOption = categories.find(
              (c) => c.label?.toLowerCase() === e.category?.toLowerCase(),
            ) || {
              label: e.category,
              value: e.expense_category_id,
            };
            let subcategoryOption = null;
            if (categoryOption?.value) {
              const loadedSubs = subcategories[categoryOption.value] || [];
              subcategoryOption = loadedSubs.find(
                (sub) =>
                  sub.label?.toLowerCase() === e.sub_category?.toLowerCase(),
              );
            }
            if (!subcategoryOption && e.sub_category) {
              subcategoryOption = {
                label: e.sub_category,
                value: e.expense_sub_category_id || e.sub_category,
              };
            }

            return {
              id: e.id,
              paymentMethod: e.approval_configuration_id
                ? {
                    value: e.approval_configuration_id,
                    label:
                      e.approval_configuration_name ||
                      e.approval_configuration_id,
                  }
                : null,
              category: categoryOption,
              subcategory: subcategoryOption,
              amount: e.expense_amount || "",
              _destroyed: false,
              new_created: false,
            };
          }) || expenseRequestInitialValues.expenses,
      }}
      enableReinitialize
      validate={(values) => {
        setAction(values?._action);
      }}
      validationSchema={action === "draft" ? null : validationSchema}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        try {
          const isDraft = values._action === "draft";

          let nextStatus = "pending";
          if (values._action === "complete") {
            nextStatus = "completed";
          } else if (isDraft) {
            nextStatus = "draft";
          }

          const payload = {
            member_id: selectedMember?.id,
            comment: values.additional_comments?.trim() || "",
            dept_code: values.department_code || null,
            status: nextStatus,
            expense_informations_attributes: [
              ...values.expenses
                .filter((e) => !e._destroyed)
                .map((e) => {
                  const item = {
                    approval_configuration_id: e.paymentMethod?.value,
                    expense_category_id: e.category?.value,
                    expense_sub_category_id: e.subcategory?.value,
                    category: e.category?.label,
                    sub_category: e.subcategory?.label,
                    expense_amount: e.amount,
                  };
                  if (!e.new_created && e.id) item.id = e.id;
                  return item;
                }),
              ...values.expenses
                .filter((e) => e._destroyed && e.id)
                .map((e) => ({ id: e.id, _destroy: true })),
            ],
            upload_w9: values.upload_w9?.file
              ? await convertFileToBase64(values.upload_w9.file)
              : values.upload_w9?.url || null,
            upload_w9_file_name:
              values.upload_w9?.file?.name || values.upload_w9?.name || null,

            upload_lease_agreement: values.upload_lease_agreement?.file
              ? await convertFileToBase64(values.upload_lease_agreement.file)
              : values.upload_lease_agreement?.url || null,
            upload_lease_agreement_file_name:
              values.upload_lease_agreement?.file?.name ||
              values.upload_lease_agreement?.name ||
              null,

            upload_authorization: values.upload_authorization?.file
              ? await convertFileToBase64(values.upload_authorization.file)
              : values.upload_authorization?.url || null,
            upload_authorization_file_name:
              values.upload_authorization?.file?.name ||
              values.upload_authorization?.name ||
              null,

            check_request_document: values.check_request_document?.file
              ? await convertFileToBase64(values.check_request_document.file)
              : values.check_request_document?.url || null,
            check_request_file_name:
              values.check_request_document?.file?.name ||
              values.check_request_document?.name ||
              null,

            other_document: values.other_document?.file
              ? await convertFileToBase64(values.other_document.file)
              : values.other_document?.url || null,
            other_document_file_name:
              values.other_document?.file?.name ||
              values.other_document?.name ||
              null,
            upload_receipt: values.upload_receipt?.file
              ? await convertFileToBase64(values.upload_receipt.file)
              : values.upload_receipt?.url || null,
            upload_receipt_file_name:
              values.upload_receipt?.file?.name ||
              values.upload_receipt?.name ||
              null,
          };

          let result;
          if (expenseRecord?.id) {
            result = await updateHousingExpense(payload, expenseRecord.id);
          } else {
            result = await createHousingExpense(payload);
          }

          if (result) {
            setStatus({ success: "Saved successfully" });
            onClose && onClose();
          } else {
            setStatus({
              error: "Failed to submit form. Please try again.",
            });
          }
        } catch (error) {
          setStatus({
            error: "Failed to submit form. Please try again.",
          });
        } finally {
          setSubmitting(false);
          setIsSubmittingForm(false);
        }
      }}
    >
      {({
        values,
        setFieldValue,
        status,
        isSubmitting,
        errors,
        submitForm,
        validateForm,
        setTouched,
      }) => {
        const calculateBalanceImpact = () => {
          let totalDeduct = 0;
          let totalAdd = 0;

          values.expenses.forEach((exp) => {
            if (exp._destroyed || !exp.amount) return;

            const amount = parseFloat(exp.amount) || 0;
            if (amount <= 0) return;

            // Check category name or calculation field
            const categoryName = exp.category?.label || "";

            const isAddToBalance =
              categoryName.toLowerCase().includes("add to balance") ||
              categoryName === "Balance Adjustment Add To Balance";

            if (isAddToBalance) {
              totalAdd += amount;
            } else {
              totalDeduct += amount;
            }
          });

          return { totalDeduct, totalAdd };
        };

        const { totalDeduct, totalAdd } = calculateBalanceImpact();
        const currentAuthBalance = selectedMember?.total_balance || 0;
        const estimatedBalance = currentAuthBalance - totalDeduct + totalAdd;

        const totalExpenseDisplay = totalDeduct - totalAdd;
        console.log("errors ", values);
        const housingDepositAuth = selectedMember?.expense_authorizations?.find(
          (auth) => auth.auth_type === "housing_deposit",
        );

        const totalExpenses = values.expenses.reduce((acc, exp) => {
          if (exp._destroyed) return acc;
          const amount = parseFloat(exp.amount);
          if (!isNaN(amount)) return acc + amount;
          return acc;
        }, 0);

        const firstPaymentMethod = values.expenses.find(
          (exp) => exp.paymentMethod && !exp._destroyed,
        )?.paymentMethod;

        const currentStatus = expenseRecord?.status?.toLowerCase();
        const isApproved = currentStatus === "approved";
        const isDraftOrNew = !expenseRecord || currentStatus === "draft";
        const isDenied = currentStatus === "denied";

        const mainButtonText = isApproved
          ? "Complete Request"
          : "Submit Request";
        const mainActionValue = isApproved ? "complete" : "submit";

        return (
          <Form className="row" style={{ background: "white" }}>
            <h4 className="my-4">Member Information</h4>
            <div className="col-sm-6">
              <Input
                handleChange={(e) =>
                  setFieldValue("member_name", e.target.value)
                }
                placeholder="e.g. John Doe"
                type="text"
                label="Member Name"
                name="member_name"
                disabled
                value={values.member_name}
              />
              <ErrorMessage
                name="member_name"
                component="div"
                className="text-danger small"
              />
            </div>
            <div className="col-sm-6">
              <DateInput
                label="Date of Birth"
                name="date_of_birth"
                placeholderText="Date of Birth"
                placeholder="Select Date of Birth"
                handleChange={(date) => setFieldValue("date_of_birth", date)}
                disabled
                value={values.date_of_birth}
              />
              <ErrorMessage
                name="date_of_birth"
                component="div"
                className="text-danger small"
              />
            </div>
            <div className="col-sm-6">
              <Input
                handleChange={(e) => setFieldValue("cin", e.target.value)}
                placeholder="e.g. CIN7438201"
                type="text"
                label="CIN"
                name="cin"
                disabled
                value={values.cin}
              />
              <ErrorMessage
                name="cin"
                component="div"
                className="text-danger small"
              />
            </div>
            <div className="col-sm-6">
              <Input
                label="Health Plan"
                name="health_plan"
                type="text"
                value={
                  values.health_plan?.label ||
                  values.health_plan?.name ||
                  values.health_plan ||
                  ""
                }
                disabled
                placeholder="Health Plan"
              />
            </div>

            <div className="col-sm-6">
              <Input
                label="County"
                name="county"
                type="text"
                value={values.county}
                disabled
                placeholder="County"
              />
              <ErrorMessage
                name="county"
                component="div"
                className="text-danger small"
              />
            </div>
            <div className="col-sm-6">
              <Input
                handleChange={(e) =>
                  setFieldValue("department_code", e.target.value)
                }
                placeholder="e.g. DC-101"
                type="number"
                label="Department Code"
                disabled={isApproved}
                isRequired
                name="department_code"
                value={values.department_code}
              />
              <ErrorMessage
                name="department_code"
                component="div"
                className="text-danger small"
              />
            </div>
            <div className="col-sm-6">
              <Input
                label="CSS Manager"
                name="case_manager"
                type="text"
                value={
                  values.case_manager?.label ||
                  `${selectedMember?.cs_manager?.first_name ||
                    ""} ${selectedMember?.cs_manager?.last_name || ""}`.trim()
                }
                disabled
                placeholder="CSS Manager"
              />
            </div>
            <div className="col-sm-6">
              <Input
                handleChange={(e) =>
                  setFieldValue("landlord_name", e.target.value)
                }
                placeholder="e.g. Jane Smith"
                type="text"
                label="Landlord Name"
                name="landlord_name"
                disabled
                value={values.landlord_name}
              />
              <ErrorMessage
                name="landlord_name"
                component="div"
                className="text-danger small"
              />
            </div>
            <hr className="mt-4" />
            <h4 className="my-4">Authorizations</h4>
            <div className="col-sm-12 mb-3"></div>
            <div className="col-sm-12">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Auth Type</th>
                    <th>Auth No</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Approved No Days</th>
                    <th>Authorized Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedMember?.expense_authorizations
                    ?.filter(
                      (auth) => auth.expense_status?.toLowerCase() == "open",
                    )
                    .map((auth, index) => (
                      <tr key={index}>
                        <td>{formatString(auth.auth_type)}</td>
                        <td>{auth.auth_no}</td>
                        <td>{auth.start_date}</td>
                        <td>{auth.end_date}</td>
                        <td>{auth.approved_days}</td>
                        <td>
                          ${parseFloat(auth.authorized_amount || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}

                  {(!selectedMember?.expense_authorizations ||
                    selectedMember.expense_authorizations.length === 0) && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        No authorizations found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <hr className="mt-4" />
            <h4 className="my-4">Expense Information</h4>
            <div className="col-sm-6 mb-3">
              <AsyncSelect
                name="paymentMethod"
                label="Payment Method (Applies to all expenses)"
                className="mb-1"
                disabled={isApproved}
                options={paymentMethods}
                isRequired
                handleChange={(obj) => {
                  values.expenses.forEach((expense, index) => {
                    if (!expense._destroyed) {
                      setFieldValue(`expenses.${index}.paymentMethod`, obj);
                    }
                  });
                }}
                loadOptions={(inputValue) =>
                  new Promise((resolve) =>
                    debouncePaymentMethod(inputValue, resolve),
                  )
                }
                placeholder="Select Payment Method"
                selectedValue={firstPaymentMethod?.value}
                // disabled={values.expenses.length === 0 || loadingCategories}
              />
            </div>
            <FieldArray name="expenses">
              {({ push, remove }) => (
                <>
                  {values.expenses.map((expense, index) => {
                    // const categoryKey = expense.category?.label
                    //   ? normalizeKey(expense.category.label)
                    //   : expense.category?.value
                    //     ? normalizeKey(expense.category.value.toString())
                    //     : null;
                    const catId = expense.category?.value;
                    const currentSubOptions = catId
                      ? subcategories[catId] || []
                      : [];

                    return (
                      <div
                        className={`row mb-4 ${
                          expense._destroyed ? "opacity-50" : ""
                        }`}
                        key={index}
                      >
                        {expense._destroyed && (
                          <div className="col-12 mb-2">
                            <div className="alert alert-warning py-2">
                              <small>
                                This expense will be deleted when you save the
                                form
                              </small>
                            </div>
                          </div>
                        )}
                        {index > 0 && (
                          <div className="d-flex w-100 gap-3 align-items-center justify-content-end">
                            <Trash
                              size={20}
                              className="cursor-pointer opacity-50"
                              onClick={() => {
                                if (expense?.id) {
                                  setFieldValue(
                                    `expenses.${index}._destroyed`,
                                    true,
                                  );
                                  setFieldValue(
                                    `expenses.${index}._destroy`,
                                    true,
                                  );
                                } else {
                                  remove(index);
                                }
                              }}
                            />
                          </div>
                        )}
                        <div className="col-sm-6">
                          <AsyncSelect
                            name={`expenses.${index}.category`}
                            label="Category"
                            className="mb-1"
                            isRequired
                            options={categories}
                            handleChange={(obj) => {
                              if (expense._destroyed) return;
                              setFieldValue(`expenses.${index}.category`, obj);
                              setFieldValue(
                                `expenses.${index}.subcategory`,
                                null,
                              );
                              if (obj?.value)
                                fetchExpenseSubCategories(obj.value); // ← using ID
                            }}
                            loadOptions={(inputValue) =>
                              new Promise((resolve) =>
                                debounceCategoryDataCallBack(
                                  inputValue,
                                  resolve,
                                ),
                              )
                            }
                            placeholder="Select"
                            selectedValue={values.expenses[index].category}
                            // disabled={loadingCategories || isApproved}
                            disabled={
                              !!values.expenses[index]._destroyed ||
                              !firstPaymentMethod?.value ||
                              loadingCategories ||
                              isApproved
                            }
                          />
                          <ErrorMessage
                            name={`expenses.${index}.category`}
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                        <div className="col-sm-6">
                          <AsyncSelect
                            name={`expenses.${index}.subcategory`}
                            label="Subcategory"
                            isRequired
                            className="mb-1"
                            options={currentSubOptions}
                            handleChange={(obj) => {
                              if (values.expenses[index]._destroyed) return;
                              setFieldValue(
                                `expenses.${index}.subcategory`,
                                obj,
                              );
                            }}
                            loadOptions={(inputValue) => {
                              const currentCategoryId =
                                values.expenses?.[index]?.category?.value;

                              return new Promise((resolve) =>
                                debounceSubCategoryDataCallBack(
                                  inputValue,
                                  currentCategoryId,
                                  resolve,
                                ),
                              );
                            }}
                            placeholder="Select"
                            selectedValue={values.expenses[index].subcategory}
                            disabled={
                              !!values.expenses[index]._destroyed ||
                              !values.expenses[index].category?.value ||
                              loadingCategories ||
                              isApproved
                            }
                          />
                          <ErrorMessage
                            name={`expenses.${index}.subcategory`}
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                        <div className="col-sm-6">
                          <Input
                            handleChange={(e) => {
                              const value = e.target.value;
                              if (/^\d*$/.test(value)) {
                                setFieldValue(
                                  `expenses.${index}.amount`,
                                  e.target.value,
                                );
                              }
                            }}
                            placeholder="e.g. 100"
                            type="text"
                            inputMode="numeric"
                            label="Expense Amount"
                            isRequired
                            name={`expenses.${index}.amount`}
                            value={values.expenses[index].amount}
                            disabled={
                              !!values.expenses[index]._destroyed || isApproved
                            }
                          />
                          <ErrorMessage
                            name={`expenses.${index}.amount`}
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </div>
                    );
                  })}
                  <div className="col-sm-12">
                    <button
                      className="secondary-btn w-100 mt-4"
                      type="button"
                      onClick={() =>
                        push({
                          paymentMethod: firstPaymentMethod || null,
                          category: null,
                          subcategory: null,
                          amount: "",
                          startDate: null,
                          endDate: null,
                        })
                      }
                      disabled={loadingCategories}
                    >
                      <Plus size={18} /> Add Expense
                    </button>
                  </div>
                </>
              )}
            </FieldArray>
            <div className="col-sm-12 mt-3">
              <div
                className="p-3 border rounded"
                style={{ backgroundColor: "#f8f9fa" }}
              >
                <h5>Housing Expense Summary</h5>
                <p>
                  <strong>Current Balance:</strong> $
                  {(currentAuthBalance || 0).toFixed(2)}
                </p>
                <p>
                  <strong>Total Expense Amount:</strong> ${totalAdd.toFixed(2)}
                </p>
                <p>
                  <strong>Estimated Balance:</strong>{" "}
                  {estimatedBalance >= 0 ? (
                    `$${(estimatedBalance || 0).toFixed(2)}`
                  ) : (
                    <span style={{ color: "red" }}>
                      -${Math.abs(estimatedBalance || 0).toFixed(2)}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <hr className="mt-4" />
            <h4 className="my-4">Upload Documents</h4>
            <div className="col-sm-6">
              {renderUploadBox(
                "upload_w9",
                "Upload W-9",
                setFieldValue,
                true,
                values,
                isApproved,
              )}

              <ErrorMessage
                name="upload_w9"
                component="p"
                style={{ color: "red", fontSize: "0.8rem" }}
              />
              {values?.upload_w9 && (
                <p className="text-sm text-gray-600 mt-2">
                  Selected: {values.upload_w9.name}
                </p>
              )}
              {values?.upload_w9 && expenseRecord?.upload_w9 && (
                <div className="mt-2">
                  <a
                    href={expenseRecord.upload_w9}
                    download={expenseRecord.upload_w9_file_name}
                    className="text-blue-500 cursor-pointer flex items-center gap-1"
                  >
                    <DownloadSimple size={25} />
                    {expenseRecord.upload_w9_file_name}
                  </a>
                </div>
              )}
            </div>
            <div className="col-sm-6">
              {renderUploadBox(
                "upload_lease_agreement",
                "Upload Lease Agreement",
                setFieldValue,
                isApproved ? true : false,
                values,
                false,
                expenseRecord?.status?.toLowerCase() === "approved",
              )}

              <ErrorMessage
                name="upload_lease_agreement"
                component="p"
                style={{ color: "red", fontSize: "0.8rem" }}
              />
              {values?.upload_lease_agreement && (
                <p className="text-sm text-gray-600 mt-2">
                  Selected: {values.upload_lease_agreement.name}
                </p>
              )}
              {values?.upload_lease_agreement &&
                expenseRecord?.upload_lease_agreement && (
                  <div className="mt-2">
                    <a
                      href={expenseRecord.upload_lease_agreement}
                      download={expenseRecord.upload_lease_agreement_file_name}
                      className="text-blue-500 cursor-pointer flex items-center gap-1"
                    >
                      <DownloadSimple size={25} />
                      {expenseRecord.upload_lease_agreement_file_name}
                    </a>
                  </div>
                )}
            </div>
            <div className="col-sm-6">
              {renderUploadBox(
                "upload_authorization",
                "Upload Authorization from Health Plan",
                setFieldValue,
                true,
                values,
                isApproved,
              )}

              <ErrorMessage
                name="upload_authorization"
                component="p"
                style={{ color: "red", fontSize: "0.8rem" }}
              />
              {values?.upload_authorization && (
                <p className="text-sm text-gray-600 mt-2">
                  Selected: {values.upload_authorization.name}
                </p>
              )}
              {values?.upload_authorization &&
                expenseRecord?.upload_authorization && (
                  <div className="mt-2">
                    <a
                      href={expenseRecord.upload_authorization}
                      download={expenseRecord.upload_authorization_file_name}
                      className="text-blue-500 cursor-pointer flex items-center gap-1"
                    >
                      <DownloadSimple size={25} />
                      {expenseRecord.upload_authorization_file_name}
                    </a>
                  </div>
                )}
            </div>
            <div className="col-sm-6">
              {renderUploadBox(
                "check_request_document",
                "Check Request",
                setFieldValue,
                true,
                values,
                isApproved,
              )}

              <ErrorMessage
                name="check_request_document"
                component="p"
                style={{ color: "red", fontSize: "0.8rem" }}
              />
              {values?.check_request_document && (
                <p className="text-sm text-gray-600 mt-2">
                  Selected: {values.check_request_document.name}
                </p>
              )}
              {values?.check_request_document &&
                expenseRecord?.check_request_document && (
                  <div className="mt-2">
                    <a
                      href={expenseRecord.check_request_document}
                      download={expenseRecord.check_request_file_name}
                      className="text-blue-500 cursor-pointer flex items-center gap-1"
                    >
                      <DownloadSimple size={25} />
                      {expenseRecord.check_request_file_name}
                    </a>
                  </div>
                )}
            </div>

            <div
              className={`col-sm-${
                expenseRecord?.status?.toLowerCase() === "approved" ? "6" : "12"
              }`}
            >
              {renderUploadBox(
                "other_document",
                "Other Documents",
                setFieldValue,
                true,
                values,
                isApproved,
              )}

              <ErrorMessage
                name="other_document"
                component="p"
                style={{ color: "red", fontSize: "0.8rem" }}
              />
              {values?.other_document && (
                <p className="text-sm text-gray-600 mt-2">
                  Selected: {values.other_document.name}
                </p>
              )}
              {values?.other_document && expenseRecord?.other_document && (
                <div className="mt-2">
                  <a
                    href={expenseRecord.other_document}
                    download={expenseRecord.other_document_file_name}
                    className="text-blue-500 cursor-pointer flex items-center gap-1"
                  >
                    <DownloadSimple size={25} />
                    {expenseRecord.other_document_file_name}
                  </a>
                </div>
              )}
            </div>
            {expenseRecord?.status?.toLowerCase() === "approved" && (
              <div className="col-sm-6">
                {renderUploadBox(
                  "upload_receipt",
                  "Upload Receipt",
                  setFieldValue,
                  true,
                  values,
                )}

                <ErrorMessage
                  name="upload_receipt"
                  component="p"
                  style={{ color: "red", fontSize: "0.8rem" }}
                />

                {values?.upload_receipt && (
                  <p className="text-sm text-gray-600 mt-2">
                    Selected: {values.upload_receipt.name}
                  </p>
                )}

                {values?.upload_receipt && expenseRecord?.upload_receipt && (
                  <div className="mt-2">
                    <a
                      href={expenseRecord.upload_receipt}
                      download={expenseRecord.upload_receipt_file_name}
                      className="text-blue-500 cursor-pointer flex items-center gap-1"
                    >
                      <DownloadSimple size={25} />
                      {expenseRecord.upload_receipt_file_name}
                    </a>
                  </div>
                )}
              </div>
            )}

            <hr className="mt-4" />

            <div className="col-sm-6">
              <Input
                handleChange={(e) =>
                  setFieldValue("additional_comments", e.target.value)
                }
                placeholder="Write any additional comments"
                type="text"
                label="Additional Comments"
                isRequired
                name="additional_comments"
                value={values.additional_comments}
              />
              <ErrorMessage
                name="additional_comments"
                component="div"
                className="text-danger small"
              />
            </div>
            <div className="col-sm-6">
              {!isApproved && (
                <button
                  className="primary-btn w-50"
                  type="submit"
                  disabled={
                    isSubmitting || isSubmittingForm || loadingCategories
                  }
                  style={{ marginTop: "43px" }}
                  onClick={() => setFieldValue("_action", "draft")}
                >
                  {isSubmitting ? "Saving..." : "Save as Draft"}
                </button>
              )}

              <button
                type="button"
                className={`primary-btn ${
                  isApproved ? "w-100 mt-4_5" : "w-50"
                }`}
                disabled={isSubmitting || isSubmittingForm || loadingCategories}
                onClick={async () => {
                  if (isSubmittingForm) return;
                  setFieldValue("_action", mainActionValue);

                  await new Promise((r) => setTimeout(r, 0));
                  const validationErrors = (await validateForm()) || {};
                  if (Object.keys(validationErrors).length > 0) {
                    const touchedFields = Object.keys(validationErrors).reduce(
                      (acc, key) => {
                        acc[key] = true;
                        return acc;
                      },
                      {},
                    );
                    setTouched(touchedFields);
                    return;
                  }

                  if (estimatedBalance >= 0) {
                    await submitForm();
                  } else {
                    setShowOverLimitModal(true);
                  }
                }}
              >
                {isSubmitting || isSubmittingForm
                  ? isApproved
                    ? "Completing..."
                    : "Submitting..."
                  : mainButtonText}
              </button>
            </div>
            <CustomModal
              heading={"This expense request exceeds authorized balance"}
              show={showOverLimitModal}
              onHide={() => setShowOverLimitModal(false)}
              size="sm"
            >
              <div className="row">
                <div>
                  <button
                    className="col-sm-6 secondary-btn"
                    type="button"
                    onClick={() => setShowOverLimitModal(false)}
                  >
                    Don't Proceed
                  </button>
                  <button
                    className="col-sm-6 danger-btn"
                    type="button"
                    onClick={async () => {
                      setFieldValue("_action", mainActionValue);
                      await submitForm();
                    }}
                  >
                    Proceed Anyway
                  </button>
                </div>
              </div>
            </CustomModal>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreateHousingExpenseForm;
